import * as React from 'react';
import { classNames } from 'react-scoped-css-loader';
import { Button, Modal, useToast } from 'uxp/components';
import { IContextProvider, IUserDetails } from '../../uxp'
import { generateUUID, validateAllowedFileType } from './file-upload-helper';

import './FileUploader.scss';

interface IFileReference {
    ContentLength: number;
    Metadata: {
        Account: string
        apikey: string
    }
    Name: string
    Provider: number
    Reference: string
}

type IView = "uploader" | "cropper"

const MB = 1 * 1024 * 1024;
const CHUNK_SIZE = 5 * MB;

const NoPreview = 'https://s3.ap-southeast-1.amazonaws.com/spaceworx.marketplace/assets/no-review-text.jpg'
const videoPreview = 'https://s3.ap-southeast-1.amazonaws.com/spaceworx.marketplace/assets/video-preview.png'

interface IFileUploaderProps {
    title?: string,
    actionButtonText?: string,
    uxpContext: IContextProvider,
    defaultImage?: string,
    pathToUpload: string,
    allowedTypes?: string[]
    crop?: {
      enble?: boolean,
      imageRatio?: number,
    },
    enableUrlInput?: boolean,
    afterUpload: (url: string, fileName: string) => void,
}

const FileUploader: React.FunctionComponent<IFileUploaderProps> = (props) => {

    let { defaultImage, afterUpload, pathToUpload, allowedTypes, uxpContext, title, actionButtonText } = props

    let [view, setView] = React.useState<IView>("uploader")
    let [show, setShow] = React.useState<boolean>(false)
    let [previewImage, setPreviewImage] = React.useState<string>("")
    let [data, setData] = React.useState<{ file: File | null, type: string }>({ file: null, type: '' })

    let toast = useToast();

    let [isUploading, setIsUploading] = React.useState<boolean>(false);

    let fileInputRef = React.useRef(null)
    let urlInputRef = React.useRef(null)

    React.useEffect(() => {
        if (!show) {
            setView("uploader")
            setPreviewImage(defaultImage)
            setData({ file: null, type: null })
        }
    }, [show])

    React.useEffect(() => {
        setPreviewImage(defaultImage)
    }, [defaultImage])


    function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
        try {
            setPreviewImage('')
            let selectedFiles = e.target.files

            // validate file type 
            let isAllowed = validateAllowedFileType(selectedFiles[0].type, allowedTypes)

            if (isAllowed) {
                let reader: FileReader = new FileReader()

                reader.onloadend = (evt) => {
                    if (selectedFiles[0].type.startsWith('image/')) {
                        setPreviewImage(evt.target.result as string)
                    }
                    else if (selectedFiles[0].type.startsWith('video/')) {
                        setPreviewImage(videoPreview)
                    }
                    else {
                        setPreviewImage(NoPreview)
                    }
                }
                reader.readAsDataURL(selectedFiles[0])
                setData({ file: selectedFiles[0], type: selectedFiles[0].type })
            }
            else {
                toast.error("This file type is not allowed")
            }
        }
        catch (e) {
            toast.error("Something went wrong")
        }
    }

    function addQSToURL(url: string, qs: any) {
        let result = url.lastIndexOf("?") === -1 ? url + "?" : url;
        let qsArray = [];

        if (!!url && !!qs) {
            for (let key in qs) {
                qsArray.push(`${key}=${qs[key]}`);
            }
        }
        return result + qsArray.join("&");
    }

    async function uploadFile(url: string, filename: string, apikey: string, buffer: Blob) {
        const form = new FormData();
        form.append('file', buffer, filename);

        setIsUploading(true);

        let response = await fetch(url, {
            method: "POST",
            headers: {
                "Authorization": `${apikey}`
            },
            body: form
        })

        setIsUploading(false);

        let responseText = await response.text();

        console.log('response: ', response)

        // something went wrong, no point proceeding
        if (response.status !== 200) {
            throw responseText;
        }

        return responseText;
    }

    async function onClickUpload() {
        return new Promise<any>(async (done, nope) => {
            try {
                if (data.file) {
                    let file = data.file
                    let name = file.name
                    let ext = name.slice((Math.max(0, name.lastIndexOf(".")) || Infinity) + 1)
                    name = "file-" + generateUUID() + '.' + ext
                    let baseUrl = uxpContext.lucyUrl + 'uploadcontent/' + pathToUpload
                    let url = addQSToURL(baseUrl, { filename: name })

                    let apikey = uxpContext.apiKey

                    uploadFile(url, name, apikey, file);

                    let downloadUrl = uxpContext.lucyUrl + 'content/' + pathToUpload + name

                    afterUpload(downloadUrl, name);
                    setShow(false)

                }
                else {
                    toast.error("Please select a file or enter a url")
                    nope("No files selected")
                }
            } catch (e) {
                console.error(e)
                toast.error("Unable to upload the file. Something went wrong")
                nope("err")
            }
        })
    }

    const headerContent = <div className="file-upload-header">
        <div className="title">
            {/* <div className="icon"></div> */}
            <div className="text">{title || "File Uploader"}</div>
        </div>
    </div>

    return (<>
        <div className="uploader-content" onClick={() => setShow(true)} >
            {props.children}
        </div>
        <Modal
            title={title || "File Uploader"}
            show={show}
            onClose={() => { setShow(false) }}
            className="file-uploader"
            headerContent={headerContent}
            backgroundDismiss={false}
        >

            {/* uploader */}
            {view == "uploader" &&
                <div className={classNames("image-uploader")} >

                    <div className="uploader">
                        <input type="file" name="" id="" className="file-input"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                        />
                        <div className="preview">
                            {
                                previewImage && previewImage.trim().length > 0 ?
                                    <div style={{ backgroundImage: `url(${previewImage})` }} className="preview-image" ></div>
                                    :
                                    <div className={classNames("default-message")}>
                                        <div className="icon-cont">
                                            <div className="icon"></div>
                                        </div>
                                        <div className="message">Drag & Drop or Click</div>
                                    </div>
                            }
                        </div>

                    </div>

                    <div className="footer">
                        <div className="url-input"></div>

                        <Button
                            title={actionButtonText || "Upload"}
                            loadingTitle={`Uploading...`}
                            onClick={onClickUpload}
                            className="upload-btn"
                            loading={isUploading}
                            active={(data.file) ? true : false}
                        />
                    </div>
                </div>
            }
        </Modal>
    </>)

}

FileUploader.defaultProps = {
    allowedTypes: []
}

export default FileUploader

